import {
  computed,
  ref,
  watch,
  onMounted,
  nextTick,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
  shallowRef,
  provide,
  inject,
} from 'vue';
import {
  useVueTable,
  getCoreRowModel,
  SortingState,
  ColumnOrderState,
  ColumnPinningState,
  VisibilityState,
  ColumnSizingState,
  Header,
} from '@tanstack/vue-table';
import { merge, debounce, cloneDeep } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { ClickedCellInfoForTransformed, ClickedRowInfo, DblClickedRowInfo } from '@/common/utils';
import { useInternational } from '@/common/locale';
import type { ColumnWithHideInfo, CustomColumn } from '@/common/utils/types';
import {
  deepCompare,
  getUpdatedRows,
  shouldResetVirtualMeasurements,
} from '@/common/components/molecules/modernGrid/modernGrid.utils';
import {
  usePagination,
  useVirtualScroll,
  useFilter,
  useSorting,
  useSortReset,
  useExpanding,
  useColumnOrderAndPinningData,
  useColumnOrdering,
  useColumnResizing,
  useColumnVisibility,
  useRowSelection,
  useHeaderTemplate,
  useBodyTemplate,
  useColumnMenu,
  useRowMenu,
  useSettingMenu,
  useCellEvent,
  useEnvStore,
  useModernGridHelper,
  useHoverLastColumn,
  useSummary,
  useCellHelpers,
  useColumnSetting,
} from './modernGrid.uses';
import {
  GridRawRowDataType,
  GridRowDataType,
  GridColumnDef,
  GridRowInstance,
  GridHeaderInstance,
  GridDataFilter,
  GridStyleOption,
  PaginationEmitValue,
  GridDynamicStyleOption,
  GridTableInstance,
  ColumnVisibilityInfo,
  GridOptionV2,
  PaginationOption,
  GridColumnInstance,
} from './modernGrid.types';
import {
  COLUMN_RESIZING_HANDLE_BAR_SIZE,
  SCROLL_BAR_WIDTH,
  DEFAULT_HEADER_HEIGHT,
  defaultUseNumberColumn,
  defaultStyleOption,
  defaultTooltipOption,
  defaultPagination,
} from './modernGrid.define';
import {
  FILTER_SEARCH_REGISTRY_KEY,
  EXTERNAL_TEXT_SEARCH_KEY,
  EXTERNAL_FILTER_SEARCH_KEY,
  type FilterSearchExposed,
} from '../filterSearch/filterSearch.type';

export interface Props<RowDataType extends GridRawRowDataType> {
  columns: GridColumnDef<RowDataType>[];
  rows: RowDataType[];
  option?: GridOptionV2;
  selectedRow?: RowDataType;
  expandedRows?: RowDataType[];
  checkedRows?: RowDataType[];
  disabledRows?: RowDataType[];
  uncheckableRows?: RowDataType[];
  highlightedRows?: RowDataType[];
  originalColumns?: Readonly<CustomColumn[]> | CustomColumn[];
  dynamicStyles?: GridDynamicStyleOption[];
  envKey?: string;
  customClass?: {
    chipCellTooltip?: string[];
  };
  columnSize?: ColumnSizingState;
  isChangedCell?: (row: RowDataType, columnId: string) => boolean;
  emptyMessage?: string;
  /** 원본 rows가 있는데 검색/필터 결과가 0건일 때 표시할 메시지. 미지정 시 기본 "검색 결과가 없습니다." — ''로 억제 가능 */
  searchEmptyMessage?: string;
  initialSearchWord?: string;
  /** true이면 dashboard user env 스토어 사용(대시보드 위젯용). 미설정 시 공통 meta user env 사용 */
  useDashboardStore?: boolean;
}
export interface Emits<RowDataType extends GridRawRowDataType> {
  (e: 'change-pagination', value: PaginationEmitValue): void;
  (e: 'change-filter', value: GridDataFilter): void;
  (e: 'change-sorting', value: SortingState): void;
  (e: 'update:search-word', value: string): void;
  (e: 'change-column-size', value: { sizeInfo: ColumnSizingState; isCustomResized: boolean }): void;
  (e: 'change-column-visibility', value: VisibilityState): void;
  (e: 'change-column-order', value: ColumnOrderState): void;
  (e: 'change-column-pinning', value: ColumnPinningState): void;
  (e: 'click-cell', value: ClickedCellInfoForTransformed<GridRowDataType<RowDataType>>): void;
  (e: 'click-cell-chip', value: { evt: any; chips: string[]; rowIndex: number }): void;
  (e: 'click-cell-chip-more', value: any): void;
  (e: 'dblclick-row', row: DblClickedRowInfo<GridRowDataType<RowDataType>>): void;
  (e: 'click-row', rowInfo: ClickedRowInfo<GridRowDataType<RowDataType>>): void;
  (e: 'export-to-excel-from-server', value: { columns: ColumnWithHideInfo[] }): void;
  (e: 'update:checked-rows', value: RowDataType[]): void;
  (e: 'update:expanded-rows', value: RowDataType[]): void;
  (e: 'update:selected-row', value: RowDataType | undefined): void;
  (e: 'update:rows', rows: RowDataType[]): void;
}
export type Slots<RowDataType extends GridRawRowDataType> = {
  customHeader?: (slotProps: {
    item: { header: Header<GridRowDataType<RowDataType>, unknown> };
  }) => any;
  toolbar?: () => any;
  toolbarPrefix?: () => any;
  toolbarPostfix?: () => any;
  subToolbar?: () => any;
  topToolbar?: () => any;
  rowDetail?: (slotProps: { item: { row: GridRowDataType<RowDataType> } }) => any;
  tooltipContent?: () => any;
} & {
  [K in keyof RowDataType]?: (slotProps: {
    item: {
      row: RowDataType;
      rowIndex: number;
      column: GridColumnDef<RowDataType>;
      cell: RowDataType[K];
      table: GridTableInstance<RowDataType> | undefined;
      depth: number;
    };
  }) => any;
};

export const setup = <RowDataType extends GridRawRowDataType>(
  props: Props<RowDataType>,
  emit: Emits<RowDataType>,
  slots?: Slots<RowDataType>,
) => {
  const { t } = useInternational();
  const styleInfo = computed<GridStyleOption>(() =>
    merge({}, defaultStyleOption, props.option?.style),
  );
  const paginationInfo = computed<PaginationOption>(() =>
    merge({}, defaultPagination, props.option?.pagination),
  );
  const gridRef = ref<HTMLElement | null>(null);
  const summaryRef = ref<HTMLElement | null>(null);
  const useNumberColumn = computed<boolean>(
    () => props.option?.useNumberColumn || defaultUseNumberColumn,
  );
  const chipTooltipOptions = computed<'list' | 'chip'>(
    () => props.option?.tooltipOption || defaultTooltipOption,
  );
  const processedRows = shallowRef<GridRowDataType<RowDataType>[] | undefined>();
  const processedColumns = shallowRef<GridColumnDef<RowDataType>[] | undefined>();
  const previousColumnsSnapshot = shallowRef<GridColumnDef<RowDataType>[] | undefined>();
  const rowsMapById = ref<Map<string, GridRowDataType<RowDataType>> | undefined>();
  const table = ref<GridTableInstance<RowDataType> | undefined>();
  const tableRows = computed<GridRowInstance<RowDataType>[]>(
    () => table.value?.getRowModel().rows || [],
  );
  const tableColumns = computed<GridColumnInstance<RowDataType>[]>(
    () => (table.value?.getAllFlatColumns() || []) as GridColumnInstance<RowDataType>[],
  );
  const columnIds = computed<string[]>(() => tableColumns.value.map(({ id }) => id) || []);
  const headerDepth = computed<number>(() => table.value?.getHeaderGroups()?.length ?? 1);
  const hasGroupedColumns = ref(false);
  const columnSettingList = ref<ColumnVisibilityInfo[]>([]);

  const { getEnv, saveEnv, clearEnv } = useEnvStore(props.useDashboardStore ?? false);
  const { migrateEnvValue } = useModernGridHelper();
  const existOldEnvValue = ref(false);
  const cachedRowCount = ref(0);
  const cachedFirstRowId = ref<string | undefined>();
  const envKey = ref<string | undefined>();

  const {
    treeViewInfo,
    detailRowInfo,
    expandingInfo,
    groupingInfo,
    expandingKey,
    expandingColumn,
    expandedState,
    expandingOptions,
    setExpandingState,
    setGroupingState,
    isExpandedRowsExternallyChanged,
  } = useExpanding<RowDataType>({
    props,
    emit,
    table,
    rowsMapById,
    paginationInfo,
    columnIds,
    columnSettingList,
  });

  const usesVariableHeightDetailRows = computed<boolean>(
    () =>
      detailRowInfo.value.use &&
      detailRowInfo.value.detailRowHeight == null &&
      !treeViewInfo.value.use &&
      !groupingInfo.value.use,
  );

  const {
    rowSelectionInfo,
    rowSelectionState,
    highlightedRowIds,
    selectedRowId,
    clickedRowId,
    rowSelectionOptions,
    selectRow,
    onClickRow,
    setRowSelectionState,
    isCheckedRowsExternallyChanged,
    isInternalCheckedRowsUpdate,
    isRowUncheckable,
    toggleAllSelectableRows,
    toggleRowCheckbox,
    isAllSelectableRowsSelected,
    isSomeSelectableRowsSelected,
    isAllRowsUncheckable,
  } = useRowSelection<RowDataType>({
    props,
    emit,
    table,
    rowsMapById,
    expandingKey,
    treeViewInfo,
  });

  const { totalRowsHeight, virtualRowManager, virtualRows } = useVirtualScroll<RowDataType>({
    tableRows,
    gridRef,
    styleInfo,
    detailRowInfo,
    usesVariableHeightDetailRows,
  });

  const { currentPageNum, perDataCnt, paginationState, totalRowCount, paginationOptions } =
    usePagination<RowDataType>({
      emit,
      table,
      rows: processedRows,
      virtualRowManager,
      paginationInfo,
    });

  const {
    filterSearchRef,
    textFilterInfo,
    tokenFilterInfo,
    filterSearchResultMV,
    quickFilterResultMV,
    searchWord,
    filterOptions,
    globalFilter,
    filterCaches,
    autoValueItems,
    clearFilterSearchResultMV,
    clearFilterSearchToken: _clearFilterSearchToken,
  } = useFilter<RowDataType>({
    props,
    emit,
    table,
    currentPageNum,
    tableColumns,
    treeViewInfo,
  });

  // 외부 FilterSearch 인스턴스 등록/해제를 위한 registry
  const externalFilterSearchInstances = new Set<FilterSearchExposed>();

  provide(FILTER_SEARCH_REGISTRY_KEY, {
    register: (instance) => externalFilterSearchInstances.add(instance),
    unregister: (instance) => externalFilterSearchInstances.delete(instance),
  });

  // 형제 관계 FilterSearch 핸들러 (부모 컴포넌트에서 provide)
  const externalFilterSearchHandler = inject(EXTERNAL_FILTER_SEARCH_KEY, null);

  const clearFilterSearchToken = () => {
    _clearFilterSearchToken();
    externalFilterSearchInstances.forEach((instance) => {
      instance.clearFilterSearchToken?.();
    });
    externalFilterSearchHandler?.clear();
  };

  // 외부 텍스트 서치 핸들러 (부모 컴포넌트에서 provide)
  const externalTextSearchHandler = inject(EXTERNAL_TEXT_SEARCH_KEY, null);

  const clearSearchWord = () => {
    searchWord.value = '';
    externalTextSearchHandler?.clear();
    externalTextSearchHandler?.triggerSearch?.();
  };

  const { sortingInfo, sortingState, sortingOptions, createSortingMenuItems, setSortingState } =
    useSorting<RowDataType>({
      props,
      emit,
      table,
      virtualRowManager,
      envKey,
    });

  const { resetSort } = useSortReset<RowDataType>({
    table,
    envKey,
    useDashboardStore: props.useDashboardStore ?? false,
  });

  const {
    columnOrderState,
    columnOrderingInfo,
    columnPinningInfo,
    columnPinningState,
    leftColumnIds,
    rightColumnIds,
    columnOrderAndPinningOptions,
    setColumnOrderAndPinningState,
  } = useColumnOrderAndPinningData<RowDataType>({
    props,
    emit,
    table,
    expandingColumn,
    useNumberColumn,
    rowSelectionInfo,
    detailRowInfo,
    envKey,
  });

  const {
    isDragging,
    isOverNonDroppable,
    draggedLastColumnId,
    setColumnRef,
    initColumnDraggable,
    destroyColumnDraggable,
  } = useColumnOrdering<RowDataType>({
    table,
    leftColumnIds,
    rightColumnIds,
    columnOrderState,
    columnOrderingInfo,
    columnSettingList,
    styleInfo,
    expandingColumn,
  });

  const {
    columnResizingInfo,
    columnSizingState,
    columnSizingInfoState,
    resizeHandleMirrorXPosition,
    resizingOptions,
    resizeColumn,
    isVisibleResizeHandle,
    setColumnSizingState,
    getActualScrollBarWidth,
  } = useColumnResizing<RowDataType>({
    props,
    emit,
    table,
    gridRef,
    styleInfo,
    useNumberColumn,
    rowSelectionInfo,
    detailRowInfo,
    columnOrderState,
    hasGroupedColumns,
    tableColumns,
    envKey,
  });

  /**
   * 마지막 컬럼 드래깅 시 렌더링 문제를 방지하기 위해
   * visibility state 업데이트 전에 컬럼을 강제로 숨깁니다.
   *
   * @remarks
   * 마지막 컬럼을 드래깅할 때 발생하는 렌더링 이슈를 해결하기 위한 임시 조치입니다.
   * 드래깅이 완료되면 정상적인 visibility 상태로 복구됩니다.
   */
  const hideLastColumn = async (): Promise<void> => {
    if (!table.value || !draggedLastColumnId.value) {
      return;
    }

    try {
      const lastColumn = table.value.getColumn(draggedLastColumnId.value);
      if (lastColumn && lastColumn.getIsLastColumn()) {
        lastColumn.toggleVisibility(false);
        draggedLastColumnId.value = null;
        await nextTick();
      }
    } catch (error) {
      console.warn('Failed to hide last column during drag:', error);
    }
  };

  const {
    columnVisibilityInfo,
    columnVisibilityState,
    columnVisibilityOptions,
    setColumnVisibility,
    hideColumn,
    setColumnVisibilityState,
  } = useColumnVisibility<RowDataType>({
    props,
    emit,
    table,
    columnSettingList,
    envKey,
    hideLastColumn,
  });

  const { syncGridState, initColumnSetting, resetColumnSettings, updateColumnSizingState } =
    useColumnSetting<RowDataType>({
      props,
      columnSettingList,
      columnVisibilityState,
      processedColumns,
      hasGroupedColumns,
      envKey,
      useNumberColumn,
      detailRowInfo,
      rowSelectionInfo,
      getEnv,
      setColumnVisibilityState,
      setExpandingState,
      setColumnOrderAndPinningState,
      setColumnSizingState,
      setGroupingState,
      setSortingState,
      setRowSelectionState,
      setColumnVisibility,
    });

  const headerCell = useHeaderTemplate<RowDataType>({
    table,
    styleInfo,
    sortingInfo,
    columnVisibilityInfo,
    columnResizingInfo,
    columnPinningInfo,
    columnOrderingInfo,
    hasGroupedColumns,
    expandingInfo,
    expandingColumn,
  });
  const bodyCell = useBodyTemplate<RowDataType>({
    props,
    table,
    selectedRowId,
    clickedRowId,
    rowSelectionInfo,
    highlightedRowIds,
    treeViewInfo,
    detailRowInfo,
    groupingInfo,
    columnPinningInfo,
    expandingInfo,
    expandingColumn,
    styleInfo,
  });

  const {
    gridSettingMenuRef,
    gridSettingMenuItems,
    columnSettingRef,
    gridSettingInfo,
    onClickSettingBtn,
    exportExcel,
  } = useSettingMenu<RowDataType>({
    props,
    emit,
    table,
    t,
    styleGetter: {
      cellStyle: bodyCell.getBodyCellStyle,
      rowStyle: bodyCell.getBodyRowDynamicStyle,
    },
    syncGridState,
    resetColumnSettings,
    initColumnDraggable,
    envKey,
    clearFilterSearchToken,
    clearSearchWord,
  });

  const { columnMenuRef, columnMenuItems } = useColumnMenu<RowDataType>({
    table,
    columnVisibilityInfo,
    createSortingMenuItems,
    hideColumn,
    expandingColumn,
    setColumnSizingState,
  });

  const { rowMenuInfo, rowMenuRef, rowMenuItems, onClickRowMenuBtn, onContextMenuRow } =
    useRowMenu<RowDataType>({
      props,
      emit,
    });

  const {
    tooltipItems,
    customTooltipRef,
    currentTooltipFieldName,
    currentTooltipRowData,
    currentTooltipColumnIndex,
    isChipCellClickable,
    clickCell,
    clickCount,
    clickChip,
    hideTooltip,
    clickCountWithFieldData,
    onClickTooltipItem,
  } = useCellEvent<RowDataType>(emit);

  const cloneHeight = computed(
    () =>
      totalRowsHeight.value +
      (styleInfo.value.header?.height ?? DEFAULT_HEADER_HEIGHT) * headerDepth.value,
  );
  const { addLastColumnEvents, addRef, removeLastColumnEvents } = useHoverLastColumn(cloneHeight);

  const {
    summaryInfo,
    hasSummaryRow,
    getSummaryDisplayValue,
    getSummaryCellAlignClass,
    getSummaryCellStyle,
  } = useSummary<RowDataType>({
    props,
    table,
    tableColumns,
  });

  const gridWrapperClasses = computed(() => {
    const { showBorder } = styleInfo.value || {};
    const useTextFilterOption = textFilterInfo.value?.use && !textFilterInfo.value?.hideInput;
    const { use: useTokenFilterOption } = tokenFilterInfo.value || {};
    const { use: usePaginationOption } = paginationInfo.value || {};
    const { use: useSettingMenuOption } = gridSettingInfo.value || {};

    const noToolbar = !useTokenFilterOption && !useTextFilterOption && !useSettingMenuOption;
    const oneToolbar =
      (!useTokenFilterOption && useTextFilterOption) ||
      useTokenFilterOption !== useTextFilterOption ||
      useSettingMenuOption;
    const twoToolbar = useTokenFilterOption && useTextFilterOption;
    const noToolbarOrPagination = noToolbar && !usePaginationOption;
    const onlyPagination = noToolbar && usePaginationOption;
    const onlyOneToolbar = oneToolbar && !usePaginationOption;
    const oneToolbarAndPagination = oneToolbar && usePaginationOption;
    const onlyTwoToolbar = twoToolbar && !usePaginationOption;
    const twoToolbarAndPagination = twoToolbar && usePaginationOption;

    return [
      'modern-grid__wrapper',
      {
        'none-border': !showBorder,
        'no-use-filter': !noToolbar,
        'no-use-toolbar-and-pagination': noToolbarOrPagination,
        'use-only-pagination': onlyPagination,
        'use-only-one-toolbar': onlyOneToolbar,
        'use-one-toolbar-and-pagination': oneToolbarAndPagination,
        'use-only-two-toolbar': onlyTwoToolbar,
        'use-two-toolbar-and-pagination': twoToolbarAndPagination,
        'use-summary': hasSummaryRow.value,
      },
    ];
  });

  /**
   * 툴바 표시 여부
   * textFilter, tokenFilter, gridSetting, 또는 toolbar 관련 슬롯이 하나라도 있으면 표시
   * textFilter.hideInput이 true인 경우 검색창 UI가 숨겨지므로 툴바 표시 조건에서 제외
   */
  const showToolbar = computed(() => {
    const hasTextFilter = textFilterInfo.value.use && !textFilterInfo.value.hideInput;
    const hasFilter = hasTextFilter || tokenFilterInfo.value.use;
    const hasGridSetting = gridSettingInfo.value.use;
    const hasQuickFilter =
      tokenFilterInfo.value?.quickFilter?.use &&
      (tokenFilterInfo.value.quickFilter.elements?.length ?? 0) > 0;
    const hasSlot = !!(slots?.toolbar || slots?.toolbarPrefix || slots?.toolbarPostfix);

    return hasFilter || hasGridSetting || hasQuickFilter || hasSlot;
  });

  const {
    getCellColumnMeta,
    getHeaderColumnMeta,
    getCellRendererType,
    isCellClickable,
    getByteCellTitle,
    getFormattedByteValue,
    getToSecCellTitle,
    getToSecCellValue,
    getDigitCellTitle,
    getDigitCellValue,
  } = useCellHelpers<RowDataType>();

  const getUseHeaderCellOrderingFromHeader = (header: GridHeaderInstance<RowDataType>): boolean =>
    headerCell.getUseHeaderCellOrdering(header.column as GridColumnInstance<RowDataType>);

  const getIsVisibleOrderingHandleFromHeader = (header: GridHeaderInstance<RowDataType>): boolean =>
    headerCell.getIsVisibleOrderingHandle(header.column as GridColumnInstance<RowDataType>);

  const getSortingBtnIconFromHeader = (header: GridHeaderInstance<RowDataType>) =>
    headerCell.getSortingBtnIcon(header.column as GridColumnInstance<RowDataType>);

  const onClickHeaderCellFromHeader = ({
    header,
    e,
  }: {
    header: GridHeaderInstance<RowDataType>;
    e: MouseEvent;
  }) => {
    const column = header.column as GridColumnInstance<RowDataType>;
    if (column.getCanSort()) {
      column.getToggleSortingHandler()?.(e);
    }
  };

  watch(
    () => props.envKey,
    async (userEnvKey) => {
      existOldEnvValue.value = userEnvKey?.includes('GRIDCOLUMNS') || false;
      if (!userEnvKey?.length || !existOldEnvValue.value) {
        envKey.value = userEnvKey;
      } else {
        const newEnvKey = userEnvKey.replace('GRIDCOLUMNS', 'GRIDSETTINGS');
        envKey.value = newEnvKey;
        const oldEnvValue = getEnv(userEnvKey);
        if (oldEnvValue?.length) {
          await saveEnv(newEnvKey, migrateEnvValue(oldEnvValue));
          await clearEnv(userEnvKey);
        }
      }

      // columns watcher의 deepCompare가 변경 없음으로 판단해 syncGridState()를 호출하지 않으므로 직접 호출해 저장된 컬럼 설정을 적용
      if (table.value) syncGridState();
    },
    { immediate: true },
  );

  watch(
    () => props.columns,
    async (newColumns) => {
      if (!newColumns?.length) {
        previousColumnsSnapshot.value = undefined;
        columnVisibilityState.value = {};
        columnSettingList.value = [];
        processedColumns.value = [];
        processedRows.value = [];
        return;
      }

      const hasColumnsChanged =
        !previousColumnsSnapshot.value || !deepCompare(newColumns, previousColumnsSnapshot.value);

      if (!hasColumnsChanged) {
        return;
      }

      initColumnSetting(newColumns);
      syncGridState();
      // 컬럼이 변경되면 드래그 기능을 다시 초기화
      await nextTick();
      initColumnDraggable();
      previousColumnsSnapshot.value = cloneDeep(newColumns);
    },
    { immediate: true, deep: true },
  );

  const rowIdMapper = (
    row: RowDataType,
    mapById: Map<string, GridRowDataType<RowDataType>>,
  ): GridRowDataType<RowDataType> => {
    const rowId = row.rowId || uuid();
    const rowWithId: GridRowDataType<RowDataType> = {
      ...row,
      rowId,
    };
    mapById.set(rowId, rowWithId);

    if (expandingKey.value && Array.isArray(row[expandingKey.value])) {
      rowWithId[expandingKey.value as keyof RowDataType] = row[expandingKey.value].map(
        (child: RowDataType) => rowIdMapper(child, mapById),
      );
    }

    return rowWithId;
  };

  const initializeData = (rows: RowDataType[]): void => {
    const mapById = new Map<string, GridRowDataType<RowDataType>>();
    processedRows.value = cloneDeep(rows.map((row) => rowIdMapper(row, mapById)));
    rowsMapById.value = mapById;
  };

  watch(
    () => props.rows,
    (rows) => {
      filterCaches.value.clear();
      if (!rows?.length || !props.columns?.length) {
        processedRows.value = [];
        rowsMapById.value = undefined;
        return;
      }

      // 데이터 업데이트 시 rowDetail 상태 유지를 위해 이전 rowsMapById 저장
      const prevRowsMap = rowsMapById.value ? new Map(rowsMapById.value) : undefined;

      initializeData(rows);
      if (treeViewInfo.value?.use || detailRowInfo.value?.use || groupingInfo.value?.use) {
        setExpandingState(prevRowsMap);
      }
      if (rowSelectionInfo.value?.enableSelect || rowSelectionInfo.value?.useCheckbox) {
        setRowSelectionState();
      }
      virtualRowManager.value.measure();
    },
    { immediate: true },
  );

  watch(
    () => props.expandedRows,
    () => {
      if (treeViewInfo.value?.use || detailRowInfo.value?.use || groupingInfo.value?.use) {
        if (isExpandedRowsExternallyChanged()) {
          setExpandingState();
        }
      }
    },
  );

  watch(
    () => props.checkedRows,
    () => {
      if (isInternalCheckedRowsUpdate.value) {
        isInternalCheckedRowsUpdate.value = false;
        return;
      }
      if (
        (rowSelectionInfo.value?.enableSelect || rowSelectionInfo.value?.useCheckbox) &&
        isCheckedRowsExternallyChanged()
      ) {
        setRowSelectionState();
      }
    },
  );

  // 행 구성 변경 시 캐시를 초기화하되, root 구성이 같은 가변 detail 확장/축소는 실측값을 보존한다.
  watch(
    [tableRows, expandedState, usesVariableHeightDetailRows],
    (
      [currentRows, currentExpandedState, currentVariableDetailMode],
      [previousRows, previousExpandedState, previousVariableDetailMode],
    ) => {
      const shouldCompareRootRows =
        currentRows.length !== previousRows.length && currentVariableDetailMode;
      const shouldReset = shouldResetVirtualMeasurements({
        currentRowCount: currentRows.length,
        previousRowCount: previousRows.length,
        currentRootRowIds: shouldCompareRootRows
          ? currentRows.filter((row) => row.depth === 0).map((row) => row.id)
          : [],
        previousRootRowIds: shouldCompareRootRows
          ? previousRows.filter((row) => row.depth === 0).map((row) => row.id)
          : [],
        expandedStateChanged: currentExpandedState !== previousExpandedState,
        variableHeightDetailModeChanged: currentVariableDetailMode !== previousVariableDetailMode,
        usesVariableHeightDetailRows: currentVariableDetailMode,
      });

      if (shouldReset) {
        virtualRowManager.value.measure();
      }
    },
  );

  // 첫 하이라이트 행을 지정한 위치(align)로 스크롤한다.
  // 확실히 보이도록 가시 여부 무관하게 정렬한다. align 기본값은 'start' — 호출부가 'end'/'center' 로 지정 가능.)
  const scrollToHighlighted = (align: 'start' | 'center' | 'end' = 'start') => {
    const targetRowId = highlightedRowIds.value.find((id) => !!id);
    if (!targetRowId) {
      return;
    }
    const targetIndex = tableRows.value.findIndex((row) => row.id === targetRowId);
    if (targetIndex < 0) {
      return;
    }
    virtualRowManager.value.scrollToIndex(targetIndex, { align });
  };

  let visibilityTimerId: ReturnType<typeof setTimeout> | null = null;

  watch(
    () => columnVisibilityState.value,
    () => {
      if (visibilityTimerId) {
        clearTimeout(visibilityTimerId);
      }
      visibilityTimerId = setTimeout(() => {
        addRef(gridRef.value);
        addLastColumnEvents();
      }, 100);
    },
    { deep: true },
  );

  watch(
    () => isDragging.value,
    async (newVal, oldVal) => {
      if (oldVal && !newVal) {
        await nextTick();
        addRef(gridRef.value);
        addLastColumnEvents();
      }
    },
  );

  const resizeHandleWidth = `${COLUMN_RESIZING_HANDLE_BAR_SIZE}px`;
  const resizeHandleHeight = ref<string | null>(null);
  const containerWidth = ref<string>('100%');
  let previousWidth = 0;
  let prevScrollBarWidth = SCROLL_BAR_WIDTH;

  const recalcIfScrollBarChanged = async () => {
    await nextTick();
    const currentScrollBarWidth = getActualScrollBarWidth();
    if (currentScrollBarWidth !== prevScrollBarWidth && previousWidth > 0) {
      prevScrollBarWidth = currentScrollBarWidth;
      setColumnSizingState(props.columnSize);
      containerWidth.value = `${previousWidth - currentScrollBarWidth}px`;
    }
  };

  watch(
    () => tableRows.value.length,
    () => {
      recalcIfScrollBarChanged();
    },
  );

  const calcResizing = (width: number) => {
    if (!gridRef.value) {
      return;
    }
    previousWidth = width;
    setColumnSizingState(props.columnSize);
    const scrollBarWidth = getActualScrollBarWidth();
    prevScrollBarWidth = scrollBarWidth;
    containerWidth.value = `${width - scrollBarWidth}px`;
    /**
     * 그리드 리사이즈 옵저버, 250617 기준 높이 관련 로직 주석 처리 합니다. #6671 참고
     */
    // const { showBorder, header, body } = styleInfo.value || {};
    // const gridHeight = showBorder ? height : height;
    // const rowHeight = body?.rowHeight || 0;
    // const headerHeight = header?.height ?? rowHeight;
    // const rowCount = table.value?.getRowCount() || 0;
    // const bodyHeight = rowHeight * rowCount;
    // const contentHeight = headerHeight + bodyHeight;

    // if (isNil(height) || !tableRows.value?.length) {
    //   resizeHandleHeight.value = null;
    // } else if (contentHeight > gridHeight) {
    //   resizeHandleHeight.value = `${contentHeight}px`;
    // } else {
    //   resizeHandleHeight.value = `${gridHeight}px`;
    // }
  };

  const debouncedCalcResizing = debounce((width: number) => {
    calcResizing(width);
  }, 200);

  const resizeObserver = new ResizeObserver((entries) => {
    const entry = entries[0];
    const { inlineSize } = entry.borderBoxSize[0];
    const width = styleInfo.value.showBorder ? inlineSize - 2 : inlineSize;
    // const height = entry.borderBoxSize[0].blockSize;

    // 탭 전환 중 remount 등으로 레이아웃 확정 전 0폭이 측정되면 width가 음수(-2)가 되어
    // containerWidth(빈 셀 너비)가 -2px로 오염되고, previousWidth가 음수로 고착되면
    // recalcIfScrollBarChanged(previousWidth > 0 가드)가 복구하지 못한다. 유효하지 않은 측정은 무시.
    if (width <= 0) {
      return;
    }

    if (previousWidth <= 0) {
      calcResizing(width);
    } else if (Math.abs(width - previousWidth) > 2) {
      debouncedCalcResizing(width);
    }
  });

  let isInit = false;

  const onContainerScroll = () => {
    if (gridRef.value && summaryRef.value) {
      summaryRef.value.scrollLeft = gridRef.value.scrollLeft;
    }
  };

  onMounted(async () => {
    table.value = useVueTable({
      get data() {
        return processedRows.value || [];
      },
      get columns() {
        return processedColumns.value || [];
      },
      getCoreRowModel: getCoreRowModel(),
      getRowId: ({ rowId }) => rowId,
      ...paginationOptions.value,
      ...filterOptions.value,
      ...sortingOptions.value,
      get getExpandedRowModel() {
        return expandingOptions.value.getExpandedRowModel;
      },
      get getGroupedRowModel() {
        return expandingOptions.value.getGroupedRowModel;
      },
      get filterFromLeafRows() {
        return expandingOptions.value.filterFromLeafRows;
      },
      get paginateExpandedRows() {
        return expandingOptions.value.paginateExpandedRows;
      },
      get manualExpanding() {
        return expandingOptions.value.manualExpanding;
      },
      get enableExpanding() {
        return expandingOptions.value.enableExpanding;
      },
      get enableGrouping() {
        return expandingOptions.value.enableGrouping;
      },
      get autoResetExpanded() {
        return expandingOptions.value.autoResetExpanded;
      },
      get groupedColumnMode() {
        return expandingOptions.value.groupedColumnMode;
      },
      get getSubRows() {
        return expandingOptions.value.getSubRows;
      },
      get getRowCanExpand() {
        return expandingOptions.value.getRowCanExpand;
      },
      get onExpandedChange() {
        return expandingOptions.value.onExpandedChange;
      },
      ...resizingOptions.value,
      ...columnVisibilityOptions.value,
      ...columnOrderAndPinningOptions.value,
      ...rowSelectionOptions.value,
      state: {
        get pagination() {
          return paginationState.value;
        },
        get globalFilter() {
          return globalFilter.value;
        },
        get sorting() {
          return sortingState.value;
        },
        get columnOrder() {
          return columnOrderState.value;
        },
        get columnPinning() {
          return columnPinningState.value;
        },
        get columnVisibility() {
          return columnVisibilityState.value;
        },
        get rowSelection() {
          return rowSelectionState.value;
        },
        get expanded() {
          return expandedState.value;
        },
        get columnSizing() {
          return columnSizingState.value;
        },
      },
      meta: {
        updateData: (rowIndex: number, columnId: string, value: any) => {
          const newRows = getUpdatedRows(props.rows, rowIndex, columnId, value);
          emit('update:rows', newRows);

          // rowSelectionState (업데이트된 rowId)를 기준으로 checkedRows 재구성
          if (
            rowSelectionInfo.value.useCheckbox &&
            processedRows.value &&
            props.checkedRows &&
            props.checkedRows.length > 0
          ) {
            const currentSelection = rowSelectionState.value;
            const newCheckedRows: RowDataType[] = [];

            processedRows.value.forEach((row, index) => {
              if (row.rowId && currentSelection[row.rowId]) {
                const targetRow: any = newRows[index];

                // newRows에 rowId가 없다면, processedRows의 ID를 주입
                if (!targetRow.rowId) {
                  targetRow.rowId = row.rowId;
                }
                newCheckedRows.push(targetRow);
              }
            });

            if (newCheckedRows.length > 0) {
              emit('update:checked-rows', newCheckedRows);
            }
          }
        },
      },
    });
    await nextTick();
    if (gridRef.value) {
      resizeObserver.observe(gridRef.value);
    }

    if (!isInit) {
      syncGridState();
      isInit = true;
    }
    initColumnDraggable();
    addRef(gridRef.value);
    addLastColumnEvents();
  });

  onBeforeUnmount(() => {
    if (visibilityTimerId) {
      clearTimeout(visibilityTimerId);
      visibilityTimerId = null;
    }
    debouncedCalcResizing.cancel();
    resizeObserver.disconnect();
    destroyColumnDraggable();
    if (table.value) {
      table.value.reset();
      table.value = undefined;
    }
    gridRef.value = null;
    rowsMapById.value = undefined;
    filterCaches.value.clear();
    processedColumns.value = undefined;
    processedRows.value = undefined;
    removeLastColumnEvents();
  });

  // keep-alive deactivate 시점에 DOM이 분리되며 브라우저가 scrollTop을 0으로 리셋하는 반면,
  // @tanstack/vue-virtual 내부 scrollOffset은 이전 값을 유지하여 가상 행이 화면 밖에 렌더링되어
  // 그리드가 비어 보이는 현상을 방지하기 위해 스크롤 위치를 보존/복원한다.

  onDeactivated(() => {
    if (gridRef.value) {
      cachedRowCount.value = tableRows.value.length;
      cachedFirstRowId.value = tableRows.value[0]?.id;
    }
  });

  onActivated(async () => {
    await nextTick();
    if (!gridRef.value) {
      return;
    }

    // 데이터가 변경되었거나 비어있는 경우 스크롤 위치 보존이 무의미하므로 맨 위로 초기화한다.
    const isDataChanged =
      cachedRowCount.value !== tableRows.value.length ||
      cachedFirstRowId.value !== tableRows.value[0]?.id;

    const scrollOffset = virtualRowManager.value.scrollOffset ?? 0;

    if (isDataChanged || scrollOffset <= 0 || !tableRows.value.length) {
      gridRef.value.scrollTop = 0;
      virtualRowManager.value.scrollOffset = 0;
      virtualRowManager.value.measure();
    } else {
      gridRef.value.scrollTop = scrollOffset;
    }
  });

  return {
    showToolbar,
    t,
    gridRef,
    summaryRef,
    resizeHandleWidth,
    resizeHandleHeight,
    containerWidth,
    gridWrapperClasses,
    onContainerScroll,
    table,
    tableRows,
    headerDepth,
    styleInfo,
    chipTooltipOptions,
    getCellColumnMeta,
    getHeaderColumnMeta,
    getCellRendererType,
    isCellClickable,
    getByteCellTitle,
    getFormattedByteValue,
    getToSecCellTitle,
    getToSecCellValue,
    getDigitCellTitle,
    getDigitCellValue,
    getUseHeaderCellOrderingFromHeader,
    getIsVisibleOrderingHandleFromHeader,
    getSortingBtnIconFromHeader,
    onClickHeaderCellFromHeader,
    syncGridState,
    resetSort,

    totalRowsHeight,
    virtualRowManager,
    virtualRows,
    scrollToHighlighted,

    gridSettingMenuRef,
    gridSettingMenuItems,
    columnSettingRef,
    gridSettingInfo,
    columnSettingList,
    onClickSettingBtn,
    exportExcel,
    setColumnVisibility,
    updateColumnSizingState,

    filterSearchRef,
    textFilterInfo,
    tokenFilterInfo,
    filterSearchResultMV,
    quickFilterResultMV,
    searchWord,
    autoValueItems,
    clearFilterSearchToken,
    clearFilterSearchResultMV,

    columnResizingInfo,
    columnOrderingInfo,
    columnSizingInfoState,
    resizeHandleMirrorXPosition,
    cloneHeight,
    resizeColumn,
    isVisibleResizeHandle,
    isDragging,
    isOverNonDroppable,
    columnMenuRef,
    columnMenuItems,
    setColumnRef,
    ...headerCell,

    treeViewInfo,
    expandingInfo,
    detailRowInfo,
    expandingColumn,
    rowSelectionInfo,
    selectRow,
    onClickRow,
    setRowSelectionState,
    isRowUncheckable,
    isAllRowsUncheckable,
    toggleAllSelectableRows,
    toggleRowCheckbox,
    isAllSelectableRowsSelected,
    isSomeSelectableRowsSelected,

    rowMenuInfo,
    rowMenuRef,
    rowMenuItems,
    onClickRowMenuBtn,
    onContextMenuRow,

    ...bodyCell,
    tooltipItems,
    customTooltipRef,
    currentTooltipFieldName,
    currentTooltipRowData,
    currentTooltipColumnIndex,
    isChipCellClickable,
    clickCount,
    clickChip,
    hideTooltip,
    clickCountWithFieldData,
    onClickTooltipItem,
    clickCell,

    paginationInfo,
    currentPageNum,
    perDataCnt,
    totalRowCount,

    summaryInfo,
    hasSummaryRow,
    getSummaryDisplayValue,
    getSummaryCellAlignClass,
    getSummaryCellStyle,
  };
};
